import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  UserLogin,
  Token,
  Article,
  ArticleCreate,
  Event,
  EventCreate,
  Album,
  AlbumCreate,
  AlbumMedia,
  Canticle,
  CanticleCreate,
  Book,
  BookCreate,
  Message,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(credentials: UserLogin): Promise<Token> {
    const response: AxiosResponse<Token> = await this.api.post(
      '/auth/login',
      credentials
    );
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/auth/me');
    return response.data;
  }

  // User endpoints (read-only for admin reference)
  async getUsers(
    skip = 0,
    limit = 100
  ): Promise<{ users: User[]; total: number }> {
    const response = await this.api.get(`/users/?skip=${skip}&limit=${limit}`);
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get(
      `/users/${userId}`
    );
    return response.data;
  }

  // Article endpoints
  async getArticles(
    skip = 0,
    limit = 100,
    publishedOnly = true
  ): Promise<{ articles: Article[]; total: number }> {
    const response = await this.api.get(
      `/articles/?skip=${skip}&limit=${limit}&published_only=${publishedOnly}`
    );
    return response.data;
  }

  async getArticle(articleId: number): Promise<Article> {
    const response: AxiosResponse<Article> = await this.api.get(
      `/articles/${articleId}`
    );
    return response.data;
  }

  async createArticle(articleData: ArticleCreate): Promise<Article> {
    const response: AxiosResponse<Article> = await this.api.post(
      '/articles/',
      articleData
    );
    return response.data;
  }

  async updateArticle(
    articleId: number,
    articleData: Partial<ArticleCreate>
  ): Promise<Article> {
    const response: AxiosResponse<Article> = await this.api.put(
      `/articles/${articleId}`,
      articleData
    );
    return response.data;
  }

  async deleteArticle(articleId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/articles/${articleId}`);
    return response.data;
  }

  // Event endpoints
  async getEvents(
    skip = 0,
    limit = 100,
    publishedOnly = true
  ): Promise<{ events: Event[]; total: number }> {
    const response = await this.api.get(
      `/events/?skip=${skip}&limit=${limit}&published_only=${publishedOnly}`
    );
    return response.data;
  }

  async getEvent(eventId: number): Promise<Event> {
    const response: AxiosResponse<Event> = await this.api.get(
      `/events/${eventId}`
    );
    return response.data;
  }

  async createEvent(eventData: EventCreate): Promise<Event> {
    const response: AxiosResponse<Event> = await this.api.post(
      '/events/',
      eventData
    );
    return response.data;
  }

  async updateEvent(
    eventId: number,
    eventData: Partial<EventCreate>
  ): Promise<Event> {
    const response: AxiosResponse<Event> = await this.api.put(
      `/events/${eventId}`,
      eventData
    );
    return response.data;
  }

  async deleteEvent(eventId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/events/${eventId}`);
    return response.data;
  }

  // Dashboard statistics
  async getDashboardStats(): Promise<{
    articles: number;
    events: number;
    albums: number;
    users: number;
  }> {
    const [articlesRes, eventsRes, albumsRes, usersRes] = await Promise.all([
      this.api.get('/articles/'),
      this.api.get('/events/'),
      this.api.get('/albums/'),
      this.api.get('/users/'),
    ]);

    return {
      articles: articlesRes.data.total || 0,
      events: eventsRes.data.total || 0,
      albums: albumsRes.data.total || 0,
      users: usersRes.data.total || 0,
    };
  }

  // Get recent articles and events for activity feed
  async getRecentActivities(): Promise<{
    articles: Article[];
    events: Event[];
  }> {
    const [articlesRes, eventsRes] = await Promise.all([
      this.api.get('/articles/?limit=10&published_only=false'),
      this.api.get('/events/?limit=10&published_only=false'),
    ]);

    return {
      articles: articlesRes.data.articles || [],
      events: eventsRes.data.events || [],
    };
  }

  // Albums
  async getAlbums(): Promise<{ albums: Album[]; total: number }> {
    const response: AxiosResponse<{ albums: Album[]; total: number }> =
      await this.api.get('/albums/');
    return response.data;
  }

  async getPublishedAlbums(): Promise<{ albums: Album[]; total: number }> {
    const response: AxiosResponse<{ albums: Album[]; total: number }> =
      await this.api.get('/albums/published');
    return response.data;
  }

  async getAlbum(albumId: number): Promise<Album> {
    const response: AxiosResponse<Album> = await this.api.get(
      `/albums/${albumId}`
    );
    return response.data;
  }

  async createAlbum(albumData: AlbumCreate): Promise<Album> {
    const response: AxiosResponse<Album> = await this.api.post(
      '/albums/',
      albumData
    );
    return response.data;
  }

  async updateAlbum(
    albumId: number,
    albumData: Partial<AlbumCreate>
  ): Promise<Album> {
    const response: AxiosResponse<Album> = await this.api.put(
      `/albums/${albumId}`,
      albumData
    );
    return response.data;
  }

  async deleteAlbum(albumId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/albums/${albumId}`);
    return response.data;
  }

  // private guessMimeType(filename: string): string {
  //   const ext = filename.toLowerCase().split('.').pop();
  //   const mimeTypes: { [key: string]: string } = {
  //     jpg: 'image/jpeg',
  //     jpeg: 'image/jpeg',
  //     png: 'image/png',
  //     gif: 'image/gif',
  //     webp: 'image/webp',
  //     mp4: 'video/mp4',
  //     webm: 'video/webm',
  //     ogg: 'video/ogg',
  //     avi: 'video/avi',
  //     mov: 'video/mov',
  //   };
  //   return mimeTypes[ext || ''] || 'application/octet-stream';
  // }

  async uploadAlbumMedia(
    albumId: number,
    file: File,
    metadata?: { title?: string; description?: string }
  ): Promise<AlbumMedia> {
    console.log(`Uploading file ${file.name} to album ${albumId}...`);
    console.log('File details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
    });

    // Basic validation
    if (!file.name || file.name.trim() === '') {
      throw new Error('File must have a valid name');
    }

    // Validate file type - reject files without proper MIME types or extensions
    if ((!file.type || file.type.trim() === '') && !file.name.includes('.')) {
      throw new Error(
        `Le fichier "${file.name}" n'est pas un fichier valide. ` +
          "Veuillez télécharger des fichiers d'image ou de vidéo depuis votre ordinateur " +
          'plutôt que des liens Google Drive ou autres services cloud.'
      );
    }

    const formData = new FormData();
    formData.append('file', file);
    if (metadata?.title) formData.append('title', metadata.title);
    if (metadata?.description)
      formData.append('description', metadata.description);

    // Get the auth token
    const token = localStorage.getItem('access_token');
    console.log('Auth token:', token ? 'Present' : 'Missing');

    try {
      // Use a separate axios instance for file uploads to avoid Content-Type conflicts
      const response: AxiosResponse<AlbumMedia> = await axios.post(
        `${API_BASE_URL}/api/v1/albums/${albumId}/media`,
        formData,
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            // Don't set Content-Type - let browser set it with boundary
          },
        }
      );
      console.log('Upload successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Upload error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });

      // Log file details for debugging
      console.error('File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
      });

      throw error;
    }
  }

  async getAlbumMedia(
    albumId: number
  ): Promise<{ media_items: AlbumMedia[]; total: number }> {
    const response: AxiosResponse<{
      media_items: AlbumMedia[];
      total: number;
    }> = await this.api.get(`/albums/${albumId}/media`);
    return response.data;
  }

  async deleteAlbumMedia(mediaId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/albums/media/${mediaId}`);
    return response.data;
  }

  // Helper methods for image URLs
  getAlbumMediaUrl(mediaId: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/albums/media/${mediaId}/file`;
  }

  getAlbumCoverUrl(albumId: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/albums/${albumId}/cover`;
  }

  getArticleImageUrl(articleId: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/articles/${articleId}/image`;
  }

  async fetchArticleImageBlob(articleId: number): Promise<Blob> {
    const response = await this.api.get(`/articles/${articleId}/image`, {
      responseType: 'blob',
    });
    return response.data;
  }

  getEventImageUrl(eventId: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/events/${eventId}/image`;
  }

  // Article file endpoints
  async uploadArticleFile(articleId: number, file: File): Promise<Article> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/articles/${articleId}/files`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  async downloadArticleFile(articleId: number, fileIndex: number): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/articles/${articleId}/files/${fileIndex}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async deleteArticleFile(articleId: number, fileIndex: number): Promise<void> {
    await this.api.delete(`/articles/${articleId}/files/${fileIndex}`);
  }

  getArticleFileUrl(articleId: number, fileIndex: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/articles/${articleId}/files/${fileIndex}`;
  }

  getArticleAudioUrl(articleId: number, index?: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    const indexParam = index !== undefined ? `?index=${index}` : '';
    return `${baseURL}/articles/${articleId}/audio${indexParam}`;
  }

  async uploadArticleAudio(
    articleId: number,
    file: File
  ): Promise<{ message: string; audio_file: { path: string; caption: string }; index: number }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/articles/${articleId}/audio`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  async deleteArticleAudio(
    articleId: number,
    audioIndex: number
  ): Promise<{ message: string }> {
    const token = localStorage.getItem('access_token');
    const response = await axios.delete(
      `${API_BASE_URL}/api/v1/articles/${articleId}/audio/${audioIndex}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  // Event file endpoints
  async uploadEventFile(eventId: number, file: File): Promise<Event> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/events/${eventId}/files`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  async downloadEventFile(eventId: number, fileIndex: number): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/events/${eventId}/files/${fileIndex}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async deleteEventFile(eventId: number, fileIndex: number): Promise<void> {
    await this.api.delete(`/events/${eventId}/files/${fileIndex}`);
  }

  getEventFileUrl(eventId: number, fileIndex: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/events/${eventId}/files/${fileIndex}`;
  }

  getCanticlePdfUrl(canticleId: number): string {
    const baseURL = this.api.defaults.baseURL || `${API_BASE_URL}/api/v1`;
    return `${baseURL}/canticles/${canticleId}/pdf`;
  }

  // Canticle endpoints
  async getCanticles(
    skip = 0,
    limit = 100,
    publishedOnly = true
  ): Promise<Canticle[]> {
    const response = await this.api.get(
      `/canticles/?skip=${skip}&limit=${limit}&published_only=${publishedOnly}`
    );
    return response.data;
  }

  async getCanticle(canticleId: number): Promise<Canticle> {
    const response: AxiosResponse<Canticle> = await this.api.get(
      `/canticles/${canticleId}`
    );
    return response.data;
  }

  async createCanticle(canticleData: CanticleCreate): Promise<Canticle> {
    const response: AxiosResponse<Canticle> = await this.api.post(
      '/canticles/',
      canticleData
    );
    return response.data;
  }

  async updateCanticle(
    canticleId: number,
    canticleData: Partial<CanticleCreate>
  ): Promise<Canticle> {
    const response: AxiosResponse<Canticle> = await this.api.put(
      `/canticles/${canticleId}`,
      canticleData
    );
    return response.data;
  }

  async deleteCanticle(canticleId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/canticles/${canticleId}`);
    return response.data;
  }

  async uploadCanticlePdf(
    canticleId: number,
    file: File
  ): Promise<{ message: string; pdf_path: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/canticles/${canticleId}/pdf`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  // Image upload endpoints
  async uploadArticleImage(
    articleId: number,
    file: File
  ): Promise<{ message: string; image_path: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/articles/${articleId}/image`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  async uploadEventImage(
    eventId: number,
    file: File
  ): Promise<{ message: string; image_path: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/events/${eventId}/image`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  // Book (édition) endpoints
  async getBooks(
    skip = 0,
    limit = 100,
    publishedOnly = true,
    forSale = false
  ): Promise<Book[]> {
    const response = await this.api.get(
      `/books/?skip=${skip}&limit=${limit}&published_only=${publishedOnly}&for_sale=${forSale}`
    );
    return response.data;
  }

  async getBook(bookId: number): Promise<Book> {
    const response: AxiosResponse<Book> = await this.api.get(`/books/${bookId}`);
    return response.data;
  }

  /** URL publique de la couverture d'une édition (servie depuis MinIO via le backend). */
  getBookCoverUrl(bookId: number): string {
    return `${API_BASE_URL}/api/v1/books/${bookId}/cover`;
  }

  async createBook(bookData: BookCreate): Promise<Book> {
    const response: AxiosResponse<Book> = await this.api.post('/books/', bookData);
    return response.data;
  }

  async updateBook(bookId: number, bookData: Partial<BookCreate>): Promise<Book> {
    const response: AxiosResponse<Book> = await this.api.put(
      `/books/${bookId}`,
      bookData
    );
    return response.data;
  }

  async deleteBook(bookId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/books/${bookId}`);
    return response.data;
  }

  async uploadBookPdf(
    bookId: number,
    file: File
  ): Promise<{ message: string; pdf_path: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/books/${bookId}/pdf`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  async uploadBookCover(
    bookId: number,
    file: File
  ): Promise<{ message: string; cover_image: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/books/${bookId}/cover`,
      formData,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return response.data;
  }

  // Contact endpoints
  async sendContactEmail(data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<{ message: string }> {
    const response = await this.api.post('/contact/', data);
    return response.data;
  }

  // Contact messages (admin)
  async getMessages(
    skip = 0,
    limit = 100,
    unreadOnly = false,
    archivedOnly = false
  ): Promise<Message[]> {
    const response = await this.api.get(
      `/messages/?skip=${skip}&limit=${limit}&unread_only=${unreadOnly}&archived_only=${archivedOnly}`
    );
    return response.data;
  }

  async updateMessage(
    messageId: number,
    data: { is_read?: boolean; is_archived?: boolean }
  ): Promise<Message> {
    const response: AxiosResponse<Message> = await this.api.put(
      `/messages/${messageId}`,
      data
    );
    return response.data;
  }

  async deleteMessage(messageId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/messages/${messageId}`);
    return response.data;
  }
}

export const apiService = new ApiService();
